import React from 'react';
import { cn } from '../lib/utils';
import { MapPin, Users } from 'lucide-react';

/**
 * Compact bell-curve summary card.
 * Renders a small KDE-shaped curve preview + 15/70/15 distribution bar.
 *
 * Props:
 *   name        — group label (sub-team name, location, etc.)
 *   total       — number of ratings
 *   actual      — { top: {count, pct}, middle: {count, pct}, bottom: {count, pct}, by_rating: {...} }
 *   icon        — lucide icon to show next to the title (default Users)
 *   isDark      — boolean theme flag
 */
const gaussian = (x, mean, sigma) =>
  Math.exp(-0.5 * Math.pow((x - mean) / sigma, 2));

export const BellCurveMiniCard = ({
  name,
  total = 0,
  actual = {},
  icon: Icon = Users,
  isDark = false,
}) => {
  const topPct = actual?.top?.pct || 0;
  const middlePct = actual?.middle?.pct || 0;
  const bottomPct = actual?.bottom?.pct || 0;
  const topCount = actual?.top?.count || 0;
  const middleCount = actual?.middle?.count || 0;
  const bottomCount = actual?.bottom?.count || 0;
  const byRating = actual?.by_rating || {};

  // Tiny KDE curve
  const W = 180, H = 60;
  const pad = { top: 4, bottom: 4, left: 4, right: 4 };
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;
  const baseY = pad.top + cH;

  const counts = [
    0,
    parseInt(byRating['1'] || 0),
    parseInt(byRating['2'] || 0),
    parseInt(byRating['3'] || 0),
    parseInt(byRating['4'] || 0),
    parseInt(byRating['5'] || 0),
  ];

  const numPts = 80;
  const bandwidth = 0.55;
  const pts = [];
  let maxY = 0;
  for (let i = 0; i <= numPts; i++) {
    const t = i / numPts;
    const x = 0.5 + t * 4.0;
    let density = 0;
    for (let r = 1; r <= 5; r++) {
      density += counts[r] * gaussian(x, r, bandwidth);
    }
    if (density > maxY) maxY = density;
    pts.push({ x, density });
  }
  if (maxY === 0) maxY = 1;

  const toSvgX = (v) => pad.left + ((v - 0.5) / 4.0) * cW;
  const toSvgY = (v) => pad.top + cH - (v / maxY) * cH * 0.85;
  const z1x = toSvgX(2.5);
  const z2x = toSvgX(3.5);

  const curvePath = pts
    .map((p, i) => {
      const sx = toSvgX(p.x);
      const sy = toSvgY(p.density);
      return i === 0 ? `M ${sx} ${sy}` : `L ${sx} ${sy}`;
    })
    .join(' ');

  const makeZonePath = (startX, endX) => {
    const zonePts = pts.filter(
      (p) => toSvgX(p.x) >= startX && toSvgX(p.x) <= endX
    );
    if (zonePts.length === 0) return '';
    let path = `M ${Math.max(startX, toSvgX(zonePts[0].x))} ${baseY}`;
    zonePts.forEach((p) => {
      path += ` L ${toSvgX(p.x)} ${toSvgY(p.density)}`;
    });
    path += ` L ${Math.min(endX, toSvgX(zonePts[zonePts.length - 1].x))} ${baseY} Z`;
    return path;
  };

  const isEmpty = total === 0;

  // Distance from target — used to color the warning indicator
  const topGap = topPct - 15;
  const middleGap = middlePct - 70;
  const bottomGap = bottomPct - 15;
  const calibrationGap = Math.abs(topGap) + Math.abs(middleGap) + Math.abs(bottomGap);
  const calibrationStatus =
    calibrationGap <= 15 ? 'aligned' : calibrationGap <= 30 ? 'drifting' : 'misaligned';
  const calibrationColor = {
    aligned: 'bg-emerald-500',
    drifting: 'bg-amber-500',
    misaligned: 'bg-red-500',
  }[calibrationStatus];

  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-shadow hover:shadow-md',
        isDark ? 'border-slate-700 bg-slate-800/70' : 'border-slate-200 bg-white',
        isEmpty && 'opacity-60'
      )}
      data-testid={`bell-curve-mini-${name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <h4
            className={cn(
              'font-semibold text-sm truncate',
              isDark ? 'text-white' : 'text-slate-800'
            )}
            title={name}
          >
            {name}
          </h4>
        </div>
        <span
          className={cn(
            'text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
            isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
          )}
        >
          {total} ratings
        </span>
      </div>

      {/* Mini curve */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full mb-3"
        style={{ maxHeight: '60px' }}
        preserveAspectRatio="none"
      >
        <path d={makeZonePath(pad.left, z1x)} fill="#ef4444" fillOpacity="0.2" />
        <path d={makeZonePath(z1x, z2x)} fill="#7c3aed" fillOpacity="0.18" />
        <path d={makeZonePath(z2x, W - pad.right)} fill="#10b981" fillOpacity="0.2" />
        <path
          d={curvePath}
          fill="none"
          stroke={isDark ? '#cbd5e1' : '#475569'}
          strokeWidth="1.5"
        />
        <line
          x1={pad.left}
          y1={baseY}
          x2={W - pad.right}
          y2={baseY}
          stroke={isDark ? '#475569' : '#cbd5e1'}
          strokeWidth="1"
        />
      </svg>

      {/* Distribution bar */}
      <div className="h-2 rounded-full overflow-hidden flex bg-slate-100 dark:bg-slate-700 mb-2">
        {bottomPct > 0 && (
          <div
            className="bg-red-500 h-full"
            style={{ width: `${bottomPct}%` }}
            title={`Bottom: ${bottomPct}% (${bottomCount})`}
          />
        )}
        {middlePct > 0 && (
          <div
            className="bg-purple-500 h-full"
            style={{ width: `${middlePct}%` }}
            title={`Middle: ${middlePct}% (${middleCount})`}
          />
        )}
        {topPct > 0 && (
          <div
            className="bg-emerald-500 h-full"
            style={{ width: `${topPct}%` }}
            title={`Top: ${topPct}% (${topCount})`}
          />
        )}
      </div>

      {/* Percentages */}
      <div className="grid grid-cols-3 gap-1 text-center text-xs">
        <div>
          <div className="font-bold text-red-600 dark:text-red-400">{bottomPct}%</div>
          <div className="text-[10px] text-slate-400 dark:text-slate-500">
            Bot · {bottomCount}
          </div>
        </div>
        <div>
          <div className="font-bold text-purple-700 dark:text-purple-400">{middlePct}%</div>
          <div className="text-[10px] text-slate-400 dark:text-slate-500">
            Mid · {middleCount}
          </div>
        </div>
        <div>
          <div className="font-bold text-emerald-600 dark:text-emerald-400">{topPct}%</div>
          <div className="text-[10px] text-slate-400 dark:text-slate-500">
            Top · {topCount}
          </div>
        </div>
      </div>

      {/* Calibration indicator */}
      {!isEmpty && (
        <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-slate-100 dark:border-slate-700">
          <span className={cn('w-1.5 h-1.5 rounded-full', calibrationColor)} />
          <span className="text-[10px] text-slate-500 dark:text-slate-400 capitalize">
            {calibrationStatus} (target 15/70/15)
          </span>
        </div>
      )}
    </div>
  );
};

export default BellCurveMiniCard;
